// src/components/UpcomingEvents/UpcomingEvents.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaImage,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaRegCalendarCheck,
} from "react-icons/fa";
import { FiX } from "react-icons/fi";

import Reveal from "../../components/Reveal/Reveal";
import { getUpcomingEvents } from "../../services/eventServices";
import { useUser } from "../../pages/Home/UserContext";
import { toAbsolutePhotoUrl } from "../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function toAbs(url) {
  const cleanUrl = String(url || "").trim();
  const absoluteUrl = cleanUrl ? toAbsolutePhotoUrl(cleanUrl) : "";

  return absoluteUrl || FALLBACK;
}

function formatDateParts(dateString) {
  if (!dateString) return null;

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return null;

  const day = date.toLocaleDateString("fr-FR", {
    day: "2-digit",
  });

  const month = date
    .toLocaleDateString("fr-FR", {
      month: "short",
    })
    .replace(".", "")
    .toUpperCase();

  const year = date.toLocaleDateString("fr-FR", {
    year: "numeric",
  });

  const full = date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return {
    day,
    month,
    year,
    full,
  };
}

function DateBadge({ start, end }) {
  const startDate = formatDateParts(start);
  const endDate = formatDateParts(end);

  if (!startDate) return null;

  const isRange =
    endDate &&
    (endDate.day !== startDate.day ||
      endDate.month !== startDate.month ||
      endDate.year !== startDate.year);

  return (
    <div className="flex items-center gap-2">
      <div
        title={startDate.full}
        className="relative overflow-hidden rounded-2xl border border-white/35 bg-white/95 px-4 py-3 text-slate-950 shadow-2xl backdrop-blur-xl"
      >
        <div className="flex items-end gap-2">
          <span className="font-barlow text-4xl font-extrabold leading-none">
            {startDate.day}
          </span>

          <div className="pb-1">
            <span className="block text-xs font-extrabold uppercase tracking-[0.2em] text-primary">
              {startDate.month}
            </span>
            <span className="mt-0.5 block text-[11px] font-bold text-slate-500">
              {startDate.year}
            </span>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-primary" />
      </div>

      {isRange && endDate && (
        <div className="relative overflow-hidden rounded-2xl border border-white/35 bg-white/90 px-3 py-2 text-slate-950 shadow-xl backdrop-blur-xl">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
            au
          </div>

          <div className="mt-1 flex items-end gap-1">
            <span className="font-barlow text-2xl font-extrabold leading-none">
              {endDate.day}
            </span>
            <span className="pb-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-primary">
              {endDate.month}
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary/70" />
        </div>
      )}
    </div>
  );
}

function EventSkeletonCard() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="h-60 animate-pulse bg-slate-200 dark:bg-slate-800" />

      <div className="space-y-4 p-5">
        <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaRegCalendarCheck />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun événement à venir
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Les prochaines compétitions, animations ou rendez-vous du club
        apparaîtront ici dès qu’ils seront publiés.
      </p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="col-span-full rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
}

function EventCard({ event, index, user, onOpenImage, onMoreInfo }) {
  const cover = toAbs(event?.photo_url);
  const title = event?.titre || "Événement";
  const location = event?.lieu_event || "Lieu à préciser";
  const description = event?.description || "";

  return (
    <Reveal variant="zoom" delay={index * 90}>
      <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
        <div className="relative h-72 overflow-hidden">
          <button
            type="button"
            onClick={() => onOpenImage(event?.photo_url)}
            className="block h-full w-full"
            aria-label={`Agrandir l'image de ${title}`}
            title="Cliquer pour agrandir"
          >
            <img
              src={cover}
              alt={title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
              onError={(eventError) => {
                eventError.currentTarget.src = FALLBACK;
              }}
            />
          </button>

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/20 opacity-0 transition duration-500 group-hover:opacity-100" />

          <button
            type="button"
            onClick={() => onOpenImage(event?.photo_url)}
            className="absolute right-4 top-4 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-white/95 text-slate-950 shadow-2xl backdrop-blur-xl transition hover:scale-105 hover:bg-primary hover:text-white"
            aria-label="Agrandir l’image"
            title="Agrandir l’image"
          >
            <FaImage className="text-base" />
          </button>

          <div className="absolute left-4 top-4 z-20">
            <DateBadge start={event?.start} end={event?.end} />
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-barlow text-2xl font-extrabold leading-tight text-white drop-shadow-lg">
              {title}
            </h3>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5 pb-7">
          <div className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <FaMapMarkerAlt />
            </span>

            <span className="leading-6">{location}</span>
          </div>

          {description ? (
            <p className="mt-4 line-clamp-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : (
            <p className="mt-4 text-sm italic leading-7 text-slate-500 dark:text-slate-400">
              Description à venir…
            </p>
          )}

          {user && (
            <button
              type="button"
              onClick={onMoreInfo}
              className="mt-auto inline-flex items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white"
            >
              Plus d'infos
              <FaArrowRight className="text-xs" />
            </button>
          )}
        </div>

        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90 transition group-hover:opacity-100"
        />
      </article>
    </Reveal>
  );
}

export default function UpcomingEvents() {
  const navigate = useNavigate();
  const { user } = useUser();

  const [events, setEvents] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const upcomingEvents = await getUpcomingEvents();
        const list = Array.isArray(upcomingEvents) ? upcomingEvents : [];

        if (!mounted) return;

        setEvents(list.slice(0, 3));
      } catch (error) {
        console.error("Erreur lors de la récupération des événements :", error);

        if (mounted) {
          setEvents([]);
          setErrorMsg("Impossible de charger les prochains événements.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => {
      mounted = false;
    };
  }, []);

  const hasEvents = useMemo(() => events.length > 0, [events]);

  const openModal = (imageUrl) => {
    setSelectedImage(toAbs(imageUrl));
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const handleMoreInfo = () => {
    if (!user) return;
    navigate("/calendrier");
  };

  return (
    <section
      id="upcoming-events"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaCalendarAlt />
            Agenda du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Prochaines <span className="text-primary">dates</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            {hasEvents
              ? "Compétitions, animations, stages ou rendez-vous importants : retrouvez les prochaines dates à ne pas manquer."
              : "Aucun événement à venir pour le moment."}
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {loading &&
            Array.from({ length: 3 }).map((_, index) => (
              <EventSkeletonCard key={`event-skeleton-${index}`} />
            ))}

          {!loading && errorMsg && <ErrorState message={errorMsg} />}

          {!loading && !errorMsg && !hasEvents && <EmptyState />}

          {!loading &&
            !errorMsg &&
            events.map((event, index) => (
              <EventCard
                key={event.id || `${event.titre}-${index}`}
                event={event}
                index={index}
                user={user}
                onOpenImage={openModal}
                onMoreInfo={handleMoreInfo}
              />
            ))}
        </div>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/80 px-3 py-6 backdrop-blur-sm"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeModal}
              className="absolute -right-3 -top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/90 text-slate-700 shadow-xl transition hover:text-primary dark:bg-slate-900 dark:text-slate-200"
              aria-label="Fermer"
            >
              <FiX className="text-xl" />
            </button>

            <img
              src={selectedImage}
              alt="Agrandissement"
              className="max-h-[82vh] w-full rounded-[2rem] border border-white/20 bg-black/20 object-contain shadow-2xl"
              onError={(event) => {
                event.currentTarget.src = FALLBACK;
              }}
            />
          </div>
        </div>
      )}
    </section>
  );
}
